/**
 * The timeline's geometry, pure: every Rome day gets the same number of
 * columns, so a 23- or 25-hour day at a clock change still lines up with the
 * day headers, and a bar's columns follow from its instants and those days.
 */
export const SLOTS_PER_DAY = 8

/** Where an instant falls, in columns from the window's start (fractional), clamped to the window. */
export function columnAt(instant: number, boundaries: readonly number[]): number {
  const days = boundaries.length - 1
  const first = boundaries[0] ?? 0
  const last = boundaries[days] ?? 0
  if (instant <= first) return 0
  if (instant >= last) return days * SLOTS_PER_DAY
  let day = 0
  while (day < days - 1 && instant >= (boundaries[day + 1] ?? last)) day++
  const start = boundaries[day] ?? first
  const end = boundaries[day + 1] ?? last
  return (day + (instant - start) / (end - start)) * SLOTS_PER_DAY
}

/**
 * A bar's CSS grid lines (1-based, end exclusive), or null when it misses the
 * window. Both ends go to the nearest line. Rounding never swaps two instants,
 * so stays that do not overlap (a 09:30 return, the next pick-up at 10:00 on
 * the same car) never draw overlapping bars; rounding outwards did. Something
 * shorter than half a column keeps one column around its middle and is marked
 * `short`, so a turnaround can be left out rather than drawn over its neighbours.
 */
export function barLines(
  startsAt: string,
  endsAt: string | null,
  boundaries: readonly number[],
): { start: number; end: number; short: boolean } | null {
  const first = boundaries[0] ?? 0
  const last = boundaries[boundaries.length - 1] ?? 0
  const start = Date.parse(startsAt)
  const end = endsAt === null ? Number.POSITIVE_INFINITY : Date.parse(endsAt)
  if (end <= first || start >= last) return null
  const from = Math.round(columnAt(start, boundaries))
  const to = Math.round(columnAt(end, boundaries))
  if (to > from) return { start: from + 1, end: to + 1, short: false }
  const middle = columnAt((Math.max(start, first) + Math.min(end, last)) / 2, boundaries)
  const column = Math.min(columnCount(boundaries) - 1, Math.floor(middle))
  return { start: column + 1, end: column + 2, short: true }
}

/** The line nearest `now` (1-based), or null when now is outside the window. */
export function nowLine(now: number, boundaries: readonly number[]): number | null {
  const first = boundaries[0] ?? 0
  const last = boundaries[boundaries.length - 1] ?? 0
  if (now < first || now >= last) return null
  // A start line past the last column would add a column to the grid.
  return Math.min(columnCount(boundaries) - 1, Math.round(columnAt(now, boundaries))) + 1
}

const columnCount = (boundaries: readonly number[]) => (boundaries.length - 1) * SLOTS_PER_DAY

/**
 * The inverse of `columnAt`: the instant under an x pixel inside the grid,
 * snapped to the nearest slot line (as `barLines` rounds) and clamped to the
 * window. Each day spans `slotsPerDay` columns whatever its length, so a
 * 23- or 25-hour day is interpolated within its own boundaries.
 */
export function instantAtX(
  xFromGridLeft: number,
  gridWidthPx: number,
  boundaries: readonly number[],
  slotsPerDay: number,
): Date {
  const days = boundaries.length - 1
  const first = boundaries[0] ?? 0
  const last = boundaries[days] ?? first
  const totalColumns = days * slotsPerDay
  if (days < 1 || gridWidthPx <= 0) return new Date(first)
  const column = Math.round((xFromGridLeft / gridWidthPx) * totalColumns)
  if (column <= 0) return new Date(first)
  if (column >= totalColumns) return new Date(last)
  const day = Math.floor(column / slotsPerDay)
  const start = boundaries[day] ?? first
  const end = boundaries[day + 1] ?? last
  return new Date(start + ((column - day * slotsPerDay) / slotsPerDay) * (end - start))
}

/** The 0-based lane (vehicle row) under a y pixel inside a block's lane area, clamped to the lanes. */
export function laneAtY(yFromLanesTop: number, laneHeightPx: number, laneCount: number): number {
  if (laneCount < 1 || laneHeightPx <= 0) return 0
  return Math.min(laneCount - 1, Math.max(0, Math.floor(yFromLanesTop / laneHeightPx)))
}

/** Spreads overlapping items over as few lanes as needed, earliest first in each. */
export function lanes<T extends { startsAt: string; endsAt: string | null }>(
  items: readonly T[],
): T[][] {
  const sorted = [...items].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))
  const result: { end: number; items: T[] }[] = []
  for (const item of sorted) {
    const start = Date.parse(item.startsAt)
    const end = item.endsAt === null ? Number.POSITIVE_INFINITY : Date.parse(item.endsAt)
    const lane = result.find((candidate) => candidate.end <= start)
    if (lane) {
      lane.items.push(item)
      lane.end = end
    } else {
      result.push({ end, items: [item] })
    }
  }
  return result.map((lane) => lane.items)
}
